<template>
  <div>
    <h4 class="tw-text-sm tw-font-semibold tw-mb-4">
      {{ title }}
    </h4>
    <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
      <ClearingGroupCard
        v-for="group in groups"
        :key="group.display_order"
        :group="group"
        :all-users="allUsers"
        :users-in-other-groups="getUsersInOtherGroups(group.display_order)"
        :readonly="readonly"
        @update:name="name => handleNameUpdate(group.display_order, name)"
        @add-user="userId => handleAddUser(group.display_order, userId)"
        @remove-user="userId => handleRemoveUser(group.display_order, userId)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import ClearingGroupCard from './ClearingGroupCard.vue';

interface User {
  id: number | string;
  display_name: string;
  email: string;
}

interface ClearingGroup {
  id?: number;
  name: string;
  display_order: number;
  assignees: (number | string)[];
}

interface Props {
  groups: ClearingGroup[];
  allUsers: User[];
  title: string;
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
});

const emit = defineEmits<{
  'update:groups': [groups: ClearingGroup[]];
}>();

// Compute users in other groups (for exclusivity enforcement)
const getUsersInOtherGroups = (currentDisplayOrder: number): (number | string)[] => {
  return props.groups
    .filter(g => g.display_order !== currentDisplayOrder)
    .reduce<(number | string)[]>((acc, g) => [...acc, ...g.assignees], []);
};

// Handlers
const handleNameUpdate = (displayOrder: number, name: string) => {
  const updatedGroups = props.groups.map(g =>
    g.display_order === displayOrder ? { ...g, name } : g
  );
  emit('update:groups', updatedGroups);
};

const handleAddUser = (displayOrder: number, userId: number | string) => {
  const updatedGroups = props.groups.map(g => {
    if (g.display_order === displayOrder) {
      return {
        ...g,
        assignees: [...g.assignees, userId],
      };
    }
    return g;
  });
  emit('update:groups', updatedGroups);
};

const handleRemoveUser = (displayOrder: number, userId: number | string) => {
  const updatedGroups = props.groups.map(g => {
    if (g.display_order === displayOrder) {
      return {
        ...g,
        assignees: g.assignees.filter(id => id !== userId),
      };
    }
    return g;
  });
  emit('update:groups', updatedGroups);
};
</script>
